import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { referralsAPI } from '../api';
import { Copy, Share2, Users, Sparkles, Clock, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const MobilitierReferralsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    referralsAPI
      .mobilitierMe()
      .then((r) => setData(r.data))
      .catch(() => toast.error('No se pudo cargar el programa de referidos'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-ink-500 font-light">
          Cargando programa de referidos...
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const shareUrl = `${window.location.origin}${data.share_url}`;
  const reductionPp = data.program?.reduction_points ?? 5;
  const duration = data.program?.duration_days ?? 90;

  const copyCode = () => {
    navigator.clipboard.writeText(data.code);
    toast.success('Código copiado');
  };
  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Enlace copiado');
  };
  const shareNative = async () => {
    const text = `Te invito a publicar tu auto en Mobility con mi código ${data.code}: ${shareUrl}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Mobility · Programa Mobilitier', text, url: shareUrl });
      } catch {
        /* cancel */
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="space-y-5" data-testid="mobilitier-referrals-panel">
      {/* Hero card with current commission */}
      <Card className={`overflow-hidden border-0 ${data.bonus_active ? 'bg-lime' : 'bg-ink text-white'}`}>
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className={`text-[11px] font-comfortaa font-bold tracking-[0.22em] uppercase ${data.bonus_active ? 'text-ink-800' : 'text-lime'}`}>
                Tu comisión actual
              </div>
              <div className={`mt-2 font-comfortaa font-bold text-5xl sm:text-6xl tabular-nums ${data.bonus_active ? 'text-ink' : 'text-white'}`}>
                {Math.round((data.current_commission ?? 0.2) * 100)}%
              </div>
              <p className={`mt-2 text-sm font-light max-w-md ${data.bonus_active ? 'text-ink-700' : 'text-white/70'}`}>
                {data.bonus_active
                  ? `¡Bonus activo! Te quedan ${data.bonus_days_remaining} días con comisión reducida.`
                  : `Refiere a otros dueños y reduce tu comisión a ${Math.round(data.bonus_commission * 100)}% durante ${duration} días.`}
              </p>
            </div>
            {data.bonus_active && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-ink text-lime font-comfortaa font-bold text-xs tracking-wide">
                <Clock className="w-3.5 h-3.5" />
                {data.bonus_days_remaining}d restantes
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-ink-500 text-xs font-comfortaa font-bold tracking-wider uppercase">
              <Users className="w-4 h-4" /> Referidos
            </div>
            <div className="mt-2 font-comfortaa font-bold text-3xl text-ink">{data.referrals_count}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-ink-500 text-xs font-comfortaa font-bold tracking-wider uppercase">
              <TrendingDown className="w-4 h-4" /> Ahorro estimado
            </div>
            <div className="mt-2 font-comfortaa font-bold text-3xl text-ink">
              ${(data.savings_estimate ?? 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}
            </div>
            <div className="text-xs text-ink-500 font-light">MXN durante el bonus</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-ink-500 text-xs font-comfortaa font-bold tracking-wider uppercase">
              <Sparkles className="w-4 h-4" /> Reducción
            </div>
            <div className="mt-2 font-comfortaa font-bold text-3xl text-ink">−{reductionPp}pp</div>
            <div className="text-xs text-ink-500 font-light">durante {duration} días</div>
          </CardContent>
        </Card>
      </div>

      {/* Share code */}
      <Card>
        <CardHeader>
          <CardTitle className="font-comfortaa text-lg">Tu código de Mobilitier</CardTitle>
          <p className="text-sm text-ink-500 font-light">
            Cuando un dueño se registre con tu código y complete su primera renta, activamos automáticamente tu comisión reducida durante {duration} días.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 p-4 rounded-xl bg-ink-50 border border-ink-100">
            <div className="flex-1 font-comfortaa font-bold text-2xl tracking-[0.2em] text-ink select-all" data-testid="mob-referral-code">
              {data.code}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={copyCode}
              className="rounded-full"
              data-testid="copy-code-btn"
            >
              <Copy className="w-4 h-4 mr-1" /> Copiar
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={shareNative}
              className="rounded-full bg-lime hover:bg-lime-500 text-ink font-comfortaa font-bold"
              data-testid="share-mob-referral-btn"
            >
              <Share2 className="w-4 h-4 mr-2" /> Compartir
            </Button>
            <Button
              variant="outline"
              onClick={copyLink}
              className="rounded-full"
            >
              Copiar enlace
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Referred list */}
      {data.referrals_count > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-comfortaa text-lg">Mobilitiers referidos</CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-ink-100">
            {data.referrals.map((r) => (
              <div key={r.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-comfortaa font-bold text-ink text-sm">{r.name}</div>
                  <div className="text-xs text-ink-500 font-light">{r.email}</div>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-lime/20 text-ink-700 font-medium">
                  Activo
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MobilitierReferralsPanel;
